import { Injectable } from '@angular/core';
import { ModuleCoreService } from './module-core.service';
import { ProjectService } from '../project/project.service';
import { ApiService } from '../../api/api.service';
import { ProjectModule } from './project-module.interface';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ToastService } from '../../services/toast/toast.service';
import { Observable } from 'rxjs';
import { ModalService } from '../../services/modal/modal.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { CircularDetectionService } from '../../utilities/circular-detection/circular-detection.service';
import { ConstantsService } from '../../utilities/constants/constants.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Module service.
 *
 * The public interface for the ModuleCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class ModuleService extends ModuleCoreService {

  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected circularDetectionService: CircularDetectionService,
    protected constantsService: ConstantsService,
    private modalService: ModalService,
    protected projectService: ProjectService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    super(apiService, apiLegacyService, circularDetectionService, constantsService, projectService);
  }

  /**
   * Add new module.
   *
   * @note The new module does not contain submodules and elements yet.
   *
   * @todo Currently only works with the standard template used below, used for Project info page.
   * @todo The empty submodules array is added manually, but should be initialized server side.
   * @note The toast is added to a timeout, so it's added to the js event loop. Without, it's added
   *  to the Observable which immediately completes the toast call (only visible for split second).
   *  Due to Material Snackbar not supporting stacked toasts, it's disabled for now.
   *
   * @returns The new module.
   */
  public add(): Observable<ProjectModule> {
    const module = {
      name: this.translateService.get('newModule'),
      isTechnicalType: false
    } as Partial<ProjectModule>;
    return new Observable(observer => {
      this.apiLegacyService.createModule(module).subscribe(response => {
        this.projectService.addModule({ ...module, ...response, submodules: [] });
        observer.next(response);
        observer.complete();
        // setTimeout(() => {
        //   this.toastService.open(this.translateService.get('moduleAdded'));
        // });
      });
    });
  }

  /**
   * Delete module.
   *
   * @param module The module to delete.
   * @param [confirm=false] Whether to show confirm modal before deletion.
   */
  public delete(module: ProjectModule, confirm: boolean = false): void {
    if (confirm) {
      this.modalService.confirm({
        title: this.translateService.get('deletemoduletitle'),
        content: this.translateService.get('areyousuredeletingmodule'),
        action: this.translateService.getPlural('delete'),
        actionIcon: 'delete',
        type: 'warn'
      }).afterClosed().subscribe((click: any) => {
        if (click === 'confirm') {
          this.delete(module);
        }
      });
    } else {
      this._delete(module).subscribe(() => {
        this.toastService.open(this.translateService.get('deletedmodule'));
      });
    }
  }

  /**
   * Get module name.
   *
   * @param id The id of the module.
   * @returns The name of the module.
   */
  public getName(id: ProjectModule['_id']): ProjectModule['name'] {
    return this.getModule(id)?.name;
  }

  /**
   * Reorder modules.
   *
   * @param module The module to reorder.
   * @param order The new index.
   */
  public setOrder(module: ProjectModule, order: ProjectModule['order']): void {
    const modules = this.get();
    const moduleIndex = modules.findIndex(m => m._id === module._id);
    moveItemInArray(modules, moduleIndex, order);
    this._patchModule(module, { order: order }).subscribe(() => {
      let index = 0;
      for (const mod of modules) {
        if (index !== mod.order) {
          this.projectService.patchModule(mod, { order: index });
          mod.order = index;
        }
        index++;
      }
      this.toastService.open(this.translateService.get('modulesReordered'));
    });
  }

  /**
   * Set name (title).
   *
   * @param module The module to set the name (title) for.
   * @param name The new name (title).
   */
  public setName(module: ProjectModule, name: ProjectModule['name']): void {
    this._patchModule(module, { name: name }).subscribe(() => {
      this.toastService.open(this.translateService.get('moduleUpdated'));
    });
  }
}
